import React from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Mail, MessageSquare, ShieldCheck, HelpCircle } from "lucide-react";

const Home = () => {
  return (
    <div className="min-h-screen bg-[#FAF7F2] text-[#221A16] font-sans antialiased flex flex-col justify-between">
      {/* HEADER */}
      <header 
        data-testid="main-header"
        className="sticky top-0 z-50 bg-[#FAF7F2]/90 backdrop-blur-md border-b border-[#EAE3D9] py-4 px-4 sm:px-8 transition-all"
      >
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img 
              src="https://tagmango.com/assets/dinazs-logoblack-and-gold-0f803d2690cf72d9259a1818548ae0f2.png" 
              alt="Dinaz Prime Logo" 
              className="h-12 sm:h-16 object-contain"
            />
          </div>
          <div className="hidden sm:flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#E06D3B]">
            ZenLife Awakening
          </div>
        </div>
      </header>

      {/* MAIN CONTENT */}
      <main className="flex-grow py-12 px-4 sm:px-8 max-w-4xl mx-auto w-full flex flex-col gap-12 sm:gap-16">
        
        {/* SECTION 1 — SUCCESS CONFIRMATION */}
        <section className="text-center flex flex-col items-center gap-6 sm:gap-8">
          
          {/* Tasteful Success Checkmark Badge */}
          <div 
            data-testid="payment-success-badge"
            className="inline-flex items-center gap-2 bg-[#EBF6F0] border border-[#B7E4C7] text-[#2D6A4F] px-4 py-2 rounded-full text-xs sm:text-sm font-semibold uppercase tracking-wider shadow-sm"
          >
            <ShieldCheck className="w-4 h-4 text-[#2D6A4F]" />
            Payment Confirmed
          </div>

          <div className="flex flex-col gap-4 max-w-3xl">
            {/* Exactly: WELCOME TO ZENLIFE AWAKENING */}
            <h1 
              data-testid="welcome-title"
              className="text-4xl sm:text-5xl lg:text-6xl font-serif font-bold tracking-tight text-[#221A16] leading-[1.15]"
            >
              WELCOME TO<br />ZENLIFE AWAKENING
            </h1>

            {/* Exactly: Your ZenLife Journey Has Officially Begun. */}
            <p 
              data-testid="welcome-subtitle"
              className="text-xl sm:text-2xl font-serif font-semibold text-[#E06D3B] leading-tight italic"
            >
              Your ZenLife Journey Has Officially Begun.
            </p>
          </div>

          {/* Decorative Divider */}
          <div className="w-24 h-0.5 bg-[#EAE3D9] my-1" />

          {/* Exact text requirements: */}
          <div className="space-y-4 max-w-2xl">
            <p 
              data-testid="payment-received-message"
              className="text-lg sm:text-xl font-medium text-[#221A16] font-serif"
            >
              Your payment has been successfully received.
            </p>
            <p className="text-base sm:text-lg text-[#5C4D46] leading-relaxed">
              You have just taken an important step towards becoming stronger, healthier, more energetic and more consistent with your health.
            </p>
            <p className="text-base sm:text-lg font-medium text-[#221A16]">
              We are delighted to welcome you to the ZenLife family.
            </p>
          </div>


        </section>

        {/* SECTION 2 — WHAT TO DO NEXT */}
        <section className="flex flex-col gap-8">
          <div className="text-center flex flex-col gap-2">
            <h2 className="text-2xl sm:text-3xl font-serif font-bold text-[#221A16]">What To Do Next</h2>
            <p className="text-sm sm:text-base text-[#8C7A70] tracking-wide uppercase font-semibold">Follow these simple steps to begin your journey</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            
            {/* STEP 1 CARD — CHECK YOUR EMAIL */}
            <div 
              data-testid="step-1-email-card"
              className="bg-white border border-[#EAE3D9] p-6 sm:p-8 rounded-2xl shadow-sm hover:shadow-md transition-shadow flex flex-col gap-4 relative overflow-hidden"
            >
              <div className="absolute top-0 right-0 w-24 h-24 bg-[#FAF7F2] rounded-full -mr-8 -mt-8 flex items-center justify-center">
                <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-sm border border-[#EAE3D9]">
                  <Mail className="w-5 h-5 text-[#E06D3B]" />
                </div>
              </div>

              <div className="text-xs font-bold text-[#E06D3B] uppercase tracking-wider">
                Step 1
              </div>

              <h3 className="text-xl sm:text-2xl font-serif font-bold text-[#221A16] mt-1">
                📩 Check Your Inbox
              </h3>

              <div className="text-[#5C4D46] text-sm sm:text-base space-y-3 mt-1 leading-relaxed">
                <p>
                  We have sent you your onboarding instructions and details on how to begin your ZenLife Awakening journey from <strong className="text-[#E06D3B]">dinaz@dinazprime.com</strong>.
                </p>
                <p className="bg-[#FAF7F2] p-3 rounded-lg border border-[#EAE3D9]/60 text-xs sm:text-sm text-[#8C7A70]">
                  💡 Please also check your <strong>Spam, Promotions or Updates</strong> folder in case you don't see the email in your main inbox.
                </p>
              </div>
            </div>

            {/* STEP 2 CARD — JOIN THE COMMUNITY */}
            <div 
              data-testid="step-2-community-card"
              className="bg-white border border-[#EAE3D9] p-6 sm:p-8 rounded-2xl shadow-sm hover:shadow-md transition-shadow flex flex-col gap-4 justify-between relative overflow-hidden"
            >
              <div className="absolute top-0 right-0 w-24 h-24 bg-[#FAF7F2] rounded-full -mr-8 -mt-8 flex items-center justify-center">
                <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-sm border border-[#EAE3D9]">
                  <MessageSquare className="w-5 h-5 text-[#E87A5D]" />
                </div>
              </div>

              <div className="flex flex-col gap-4">
                <div className="text-xs font-bold text-[#E87A5D] uppercase tracking-wider">
                  Step 2
                </div>

                <h3 className="text-xl sm:text-2xl font-serif font-bold text-[#221A16] mt-1">
                  💬 Stay Connected With Us
                </h3>

                <div className="text-[#5C4D46] text-sm sm:text-base space-y-2 leading-relaxed">
                  <p>
                    Join the official ZenLife Awakening WhatsApp Group to receive:
                  </p>
                  <ul className="space-y-1 bg-[#FFF9F5] p-3 rounded-lg border border-[#EAE3D9]/60 text-xs sm:text-sm text-[#5C4D46] list-disc list-inside">
                    <li>Important community announcements</li>
                    <li>Program updates</li>
                    <li>Session reminders</li>
                    <li>News and special updates from Dinaz and the ZenLife Team</li>
                  </ul>
                </div>
              </div>

              <div className="mt-6 pt-2">
                <a 
                  href="https://chat.whatsapp.com/EcHgqcVEpts4hc8TtoI8Fp"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="join-whatsapp-button"
                  className="inline-flex w-full items-center justify-center gap-2 bg-gradient-to-r from-[#E06D3B] to-[#E87A5D] hover:from-[#C95B2B] hover:to-[#D4674A] text-white font-sans font-bold py-3.5 px-6 rounded-full shadow-md hover:shadow-lg transition-all duration-300 transform hover:-translate-y-0.5 text-center text-sm sm:text-base tracking-wider uppercase"
                >
                  <MessageSquare className="w-4 h-4 sm:w-5 h-5" />
                  JOIN WHATSAPP GROUP
                </a>
              </div>
            </div>

          </div>
        </section>
      </main>

      {/* FOOTER */}
      <footer 
        data-testid="main-footer"
        className="bg-[#1C1613] text-[#FAF7F2] py-12 px-4 sm:px-8 mt-12 border-t border-[#2D221E]"
      >
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-between gap-8">
          <div className="flex flex-col items-center md:items-start gap-4">
            <img 
              src="https://tagmango.com/assets/dinazs-logosilver-and-gold-b92179c8fe3a323d81186fa00618da03.png" 
              alt="Dinaz Prime Silver & Gold Logo" 
              className="h-10 sm:h-12 object-contain"
            />
            <div 
              data-testid="footer-creator-domain"
              className="hidden text-xs uppercase tracking-[0.2em] text-[#8C7A70]"
            >
            </div>
          </div>

          {/* Support Information (Plain Text Only, NO clickable mailto: or tel: links as requested) */}
          <div className="flex flex-col items-center md:items-end text-center md:text-right gap-2 max-w-md">
            <div className="flex items-center gap-1.5 text-xs font-semibold text-[#E06D3B] uppercase tracking-wider">
              <HelpCircle className="w-3.5 h-3.5" />
              Support & Inquiries
            </div>
            <p 
              data-testid="footer-support-info"
              className="text-sm text-[#8C7A70] leading-relaxed"
            >
              Need help or have questions? Reach out to us at <span className="text-[#FAF7F2] font-semibold">dinaz@dinazprime.com</span> or call <span className="text-[#FAF7F2] font-semibold">+91 9912222519</span>
            </p>
          </div>
        </div>

        <div className="max-w-5xl mx-auto mt-8 pt-8 border-t border-[#2D221E] text-center">
          <p className="text-xs text-[#8C7A70] tracking-wide">
            &copy; {new Date().getFullYear()} Dinaz Prime. All rights reserved. Managed by Mango AI.
          </p>
        </div>
      </footer>
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
